import { useEffect, useState } from "react";

export default function Pre({ load }) {
  const [phase, setPhase] = useState("drawing"); // drawing → graphing → fading

  useEffect(() => {
    if (!load) return;
    const t1 = setTimeout(() => setPhase("graphing"), 1200);
    const t2 = setTimeout(() => setPhase("fading"), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [load]);

  if (!load) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#0A0F1A",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 32,
        opacity: phase === "fading" ? 0 : 1,
        transition: "opacity 0.7s cubic-bezier(0.4,0,0.2,1)",
        pointerEvents: phase === "fading" ? "none" : "all",
      }}
    >
      {/* Grid background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(52,79,107,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(52,79,107,0.08) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Grain overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.025,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "128px 128px",
        }}
      />

      {/* Logo SVG */}
      <svg
        width="120"
        height="120"
        viewBox="0 0 48 48"
        fill="none"
        style={{ overflow: "visible" }}
      >
        {/* Border box */}
        <rect
          x="1"
          y="1"
          width="46"
          height="46"
          rx="5.5"
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="1.5"
          strokeDasharray="180"
          strokeDashoffset="180"
          style={{
            animation: "drawBox 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s forwards",
          }}
        />

        {/* R stem */}
        <line
          x1="12"
          y1="11"
          x2="12"
          y2="37"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="26"
          strokeDashoffset="26"
          style={{ animation: "drawLine 0.35s ease 0.6s forwards" }}
        />

        {/* R bowl */}
        <path
          d="M12 11 Q12 11 20 11 Q28 11 28 18.5 Q28 26 20 26 L12 26"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="40"
          strokeDashoffset="40"
          style={{ animation: "drawLine 0.4s ease 0.85s forwards" }}
        />

        {/* R leg */}
        <line
          x1="20"
          y1="26"
          x2="29"
          y2="37"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="14"
          strokeDashoffset="14"
          style={{ animation: "drawLine 0.25s ease 1.15s forwards" }}
        />

        {/* Growth graph */}
        <polyline
          points="29,37 33,31 37,33 41,24"
          stroke="#344F6B"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="22"
          strokeDashoffset="22"
          style={{
            animation:
              phase === "graphing" || phase === "fading"
                ? "drawGraph 0.6s cubic-bezier(0.4,0,0.2,1) forwards"
                : "none",
          }}
        />

        {/* Peak dot */}
        <circle
          cx="41"
          cy="24"
          r="2"
          fill="#344F6B"
          style={{
            opacity: 0,
            animation:
              phase === "graphing" || phase === "fading"
                ? "popDot 0.3s ease 0.55s forwards"
                : "none",
          }}
        />

        {/* Pulse ring */}
        <circle
          cx="41"
          cy="24"
          r="2"
          fill="none"
          stroke="#344F6B"
          strokeWidth="1"
          style={{
            opacity: 0,
            animation:
              phase === "graphing" || phase === "fading"
                ? "pulseRing 1s ease 0.8s infinite"
                : "none",
          }}
        />
      </svg>

      {/* Wordmark */}
      <div
        style={{
          fontFamily: "'Ubuntu', sans-serif",
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: "0.02em",
          color: "#ffffff",
          opacity: 0,
          animation: "fadeUp 0.5s ease 1s forwards",
        }}
      >
        Resova
        <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>
          labs
        </span>
      </div>

      {/* Tagline */}
      <div
        style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 300,
          fontSize: 9,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.4)",
          opacity: 0,
          animation: "fadeUp 0.5s ease 1.3s forwards",
          marginTop: -20,
        }}
      >
        Resolve · Renovate · Innovate · Results
      </div>

      {/* Loading bar */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: "rgba(52,79,107,0.15)",
        }}
      >
        <div
          style={{
            height: "100%",
            background: "#344F6B",
            animation: "loadBar 2.2s cubic-bezier(0.4,0,0.2,1) forwards",
            transformOrigin: "left",
          }}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&family=Plus+Jakarta+Sans:wght@300;400;600&display=swap');

        @keyframes drawBox {
          to { stroke-dashoffset: 0; }
        }
        @keyframes drawLine {
          to { stroke-dashoffset: 0; }
        }
        @keyframes drawGraph {
          to { stroke-dashoffset: 0; }
        }
        @keyframes popDot {
          0%   { opacity: 0; transform: scale(0); }
          70%  { opacity: 1; transform: scale(1.4); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseRing {
          0%   { opacity: 0.6; r: 2;  stroke-width: 1.2; }
          100% { opacity: 0;   r: 7;  stroke-width: 0.3; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes loadBar {
          0%   { width: 0%; }
          30%  { width: 35%; }
          70%  { width: 72%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
}
